import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Close,
    ExpandMore,
    InsertDriveFileRounded,
    PlayArrowRounded,
    Visibility,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material'
import CustomLinearProgress from '../../../../components/muiComponents/customLinearProgress'
import KinescopeVideoAnalysis from './KinescopeVideoAnalysis'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { testApiSlice } from '../../../../features/section/testSlice'
import ResultQuestion from '../../../student/examResultPage/components/ResultQuestion'
import { padTo2Digits, toMinutesAndSeconds } from '../../../../utils/functions'
import { ui, tone } from '../../../../utils/uiTokens'
import {
    PassedTestState,
    VideoNotesAnalysisState,
    WatchedVideoLessonState,
    WatchedVideoLessonTimeState,
} from '../../../../features/types'
import ConspectReviewDialog from './ConspectReviewDialog'
import { useHeadScope } from '../../../head/headScope'

interface Props {
    section: PassedTestState | WatchedVideoLessonState
    studentId: number
    isLast: boolean
    type: string
    conspect?: VideoNotesAnalysisState
}

const Section: React.FC<Props> = ({ section, studentId, type, isLast, conspect }) => {
    const { formatMessage } = useIntl()
    const { readOnly } = useHeadScope()
    const [expanded, setExpanded] = useState(false)
    const [conspectOpen, setConspectOpen] = useState(false)
    const isVideo = type === 'VIDEO'
    const conspectGraded = conspect?.point != null
    const conspectHasFiles = !!conspect?.files && conspect.files.length > 0

    const { data: videoLessonAnalysis = [], isFetching } =
        videoLessonApiSlice.useGetStudentWatchedVideoTimeAnalysisQuery(
            {
                videoLessonId: (section as WatchedVideoLessonState)
                    .videoLessonId,
                studentId,
            },
            { skip: !expanded || !isVideo },
        )

    const test = section as PassedTestState
    const [showAnswers, setShowAnswers] = useState(false)

    // Разбор тянем только когда ментор его открыл: это целый тест с вопросами
    const { data: review, isFetching: isFetchingReview } =
        testApiSlice.useGetStudentTestResultQuery(
            { topicTestId: test.topicTestId, studentId },
            { skip: !showAnswers || isVideo },
        )

    const styles = {
        wrapper: {
            p: 1.5,
            borderBottom: isLast ? '' : `1px solid ${ui.border}`,
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            cursor: 'pointer',
        },
        typeIcon: {
            width: '36px',
            height: '36px',
            flexShrink: 0,
            borderRadius: '10px',
            backgroundColor: isVideo ? ui.pinkSoft : ui.track,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
        },
        title: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            fontSize: '14px',
            color: ui.ink,
        },
        linearProgress: {
            backgroundcolor: ui.track,
            barbackgroundcolor: ui.pink,
        },
        percent: (value: number) => ({
            fontWeight: 600,
            fontSize: '13.5px',
            color: value ? ui.ink : ui.muted,
        }),
        chevron: {
            ml: 1,
            color: ui.muted,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
        panel: {
            mt: 1.5,
            ml: '48px',
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${ui.border}`,
            background: ui.surface,
        },
        panelTitle: {
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: ui.muted,
            mb: 1.5,
        },
        historyRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            fontSize: '13px',
            py: 0.75,
            borderTop: `1px solid ${ui.border}`,
        },
        historyHead: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            fontSize: '12px',
            color: ui.muted,
            pb: 0.75,
        },
        empty: {
            fontSize: '13px',
            color: ui.muted,
            py: 1,
        },
        dialogTitle: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            fontSize: '16px',
            fontWeight: 600,
            color: ui.ink,
        },
        stats: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
        },
        statLabel: {
            fontSize: '12px',
            color: ui.muted,
        },
        statValue: {
            fontSize: '18px',
            fontWeight: 600,
            color: ui.ink,
        },
        conspectRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1,
            ml: '48px',
            p: 1,
            pl: 1.5,
            pr: 1.5,
            borderRadius: '10px',
            background: ui.surface,
            border: `1px solid ${ui.border}`,
        },
        conspectBadge: {
            fontSize: '11.5px',
            fontWeight: 700,
            borderRadius: '6px',
            px: 1,
            py: 0.3,
            whiteSpace: 'nowrap' as const,
        },
        badgeGraded: { background: tone.ok.bg, color: tone.ok.text },
        badgeWaiting: { background: tone.warn.bg, color: tone.warn.text },
        badgeNone: { background: ui.track, color: ui.muted },
        conspectBtn: {
            textTransform: 'none',
            borderRadius: '9px',
            background: ui.pink,
            '&:hover': { background: ui.pinkDark },
        },
    }

    const formatDateTime = (value: Date | string) => {
        const date = new Date(value)

        return (
            `${padTo2Digits(date.getDate())}.${padTo2Digits(date.getMonth() + 1)}.${date.getFullYear()}` +
            ` ${padTo2Digits(date.getHours())}:${padTo2Digits(date.getMinutes())}`
        )
    }

    const historyRow = (analysis: WatchedVideoLessonTimeState, index: number) => {
        const date = new Date(analysis.visitedDateAndTime)

        return (
            <Box key={index} sx={styles.historyRow}>
                <Box>
                    {padTo2Digits(date.getDate())}.
                    {padTo2Digits(date.getMonth() + 1)}.{date.getFullYear()}
                </Box>
                <Box>
                    {padTo2Digits(date.getHours())}:
                    {padTo2Digits(date.getMinutes())}
                </Box>
                <Box>{toMinutesAndSeconds(analysis.watchedVideoTime)}</Box>
            </Box>
        )
    }

    const stat = (label: string, value: React.ReactNode) => (
        <Box>
            <Box sx={styles.statLabel}>{label}</Box>
            <Box sx={styles.statValue}>{value}</Box>
        </Box>
    )

    return (
        <Box sx={styles.wrapper}>
            <Box
                sx={styles.row}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={styles.typeIcon}>
                    {isVideo ? (
                        <PlayArrowRounded sx={{ color: ui.pink }} />
                    ) : (
                        <InsertDriveFileRounded sx={{ color: ui.sub }} />
                    )}
                </Box>

                <Box sx={{ width: '100%' }}>
                    <Box sx={styles.title}>
                        <Box>{section.title}</Box>
                        <Box sx={styles.percent(section.percentage)}>
                            {section.percentage}%
                        </Box>
                    </Box>

                    <CustomLinearProgress
                        variant="determinate"
                        value={section.percentage}
                        {...styles.linearProgress}
                        sx={{ height: 6, borderRadius: 999, mt: 1 }}
                    />
                </Box>

                {isVideo && (
                    <Chip
                        size="small"
                        sx={{ ml: 1.5 }}
                        label={
                            (section as WatchedVideoLessonState)
                                .watchedVideoCount
                        }
                        avatar={<Visibility />}
                    />
                )}

                <ExpandMore sx={styles.chevron} />
            </Box>

            {isVideo && (
                <Box sx={styles.conspectRow}>
                    <InsertDriveFileRounded sx={{ fontSize: 18, color: ui.muted }} />
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: ui.ink }}>
                        {formatMessage({ id: 'conspect' })}
                    </Box>
                    <Box
                        sx={{
                            ...styles.conspectBadge,
                            ...(conspectGraded
                                ? styles.badgeGraded
                                : conspect
                                    ? styles.badgeWaiting
                                    : styles.badgeNone),
                        }}
                    >
                        {conspectGraded
                            ? `${conspect?.point} ${formatMessage({ id: 'points' })}`
                            : conspect
                                ? formatMessage({ id: 'waitingReview' })
                                : formatMessage({ id: 'notUploaded' })}
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    {conspectHasFiles && (
                        <Button
                            size="small"
                            variant="contained"
                            sx={styles.conspectBtn}
                            onClick={() => setConspectOpen(true)}
                        >
                            {formatMessage({ id: readOnly ? 'open' : 'openAndEvaluate' })}
                        </Button>
                    )}
                </Box>
            )}

            <Collapse
                in={expanded}
                unmountOnExit
            >
                {isVideo ? (
                    <>
                        <Box sx={styles.panel}>
                            <Box sx={styles.panelTitle}>
                                {formatMessage({ id: 'watchHistory' })}
                            </Box>

                            {isFetching ? (
                                <CircularProgress size={20} />
                            ) : videoLessonAnalysis.length ? (
                                <>
                                    <Box sx={styles.historyHead}>
                                        <Box>{formatMessage({ id: 'visitedDate' })}</Box>
                                        <Box>{formatMessage({ id: 'visitedTime' })}</Box>
                                        <Box>{formatMessage({ id: 'duration' })}</Box>
                                    </Box>

                                    {videoLessonAnalysis.map(historyRow)}
                                </>
                            ) : (
                                <Box sx={styles.empty}>
                                    {formatMessage({ id: 'noWatchHistory' })}
                                </Box>
                            )}
                        </Box>

                        <KinescopeVideoAnalysis
                            videoLessonId={
                                (section as WatchedVideoLessonState)
                                    .videoLessonId
                            }
                            studentId={studentId}
                        />
                    </>
                ) : (
                    <Box sx={styles.panel}>
                        <Box sx={styles.panelTitle}>
                            {formatMessage({ id: 'result' })}
                        </Box>

                        {test.maxPoint ? (
                            <Box sx={styles.stats}>
                                {stat(
                                    formatMessage({ id: 'score' }),
                                    `${test.point ?? 0} / ${test.maxPoint}`,
                                )}
                                {stat(
                                    formatMessage({ id: 'result' }),
                                    `${test.percentage}%`,
                                )}
                                {stat(
                                    formatMessage({ id: 'status' }),
                                    formatMessage({
                                        id: test.finished
                                            ? 'testFinished'
                                            : 'testUnfinished',
                                    }),
                                )}
                                {test.finishedAt &&
                                    stat(
                                        formatMessage({ id: 'visitedDate' }),
                                        formatDateTime(test.finishedAt),
                                    )}
                            </Box>
                        ) : (
                            <Box sx={styles.empty}>
                                {formatMessage({ id: 'testNotPassed' })}
                            </Box>
                        )}

                        {test.maxPoint ? (
                            <Button
                                size="small"
                                variant="outlined"
                                sx={{ mt: 2 }}
                                onClick={() => setShowAnswers(true)}
                            >
                                {formatMessage({ id: 'showAnswers' })}
                            </Button>
                        ) : null}
                    </Box>
                )}
            </Collapse>

            <Dialog
                open={showAnswers}
                onClose={() => setShowAnswers(false)}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={styles.dialogTitle}>
                    <Box>
                        <Box>{section.title}</Box>

                        {review?.questions?.length ? (
                            <Box sx={styles.statLabel}>
                                {formatMessage({ id: 'correctAnswers' })}:{' '}
                                {review.countCorrect} / {review.questions.length}
                            </Box>
                        ) : null}
                    </Box>

                    <IconButton onClick={() => setShowAnswers(false)}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {isFetchingReview ? (
                        <CircularProgress size={20} />
                    ) : review?.questions?.length ? (
                        review.questions.map((question, index) => (
                            <ResultQuestion
                                key={question.id}
                                index={index}
                                question={question}
                            />
                        ))
                    ) : (
                        <Box sx={styles.empty}>
                            {formatMessage({ id: 'noData' })}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ConspectReviewDialog
                open={conspectOpen}
                onClose={() => setConspectOpen(false)}
                videonotes={conspect || null}
            />
        </Box>
    )
}

export default Section
